import confetti from 'canvas-confetti'
import { ChangeEventHandler, FormEventHandler, MouseEventHandler, useEffect, useState } from 'react'
import FormPage from '../../../Components/Form/Page/FormPage'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import isBizBrief from '../../../Logic/Form/Sequences/Biz/isBizBrief'
import { PartialBrief } from '../../../Types/Brief'
import { Collection } from '../../../Types/generated-types'
import InputView from '../InputView'
import { useEventTypesQuery } from './EventTypeInput.generated'
import throwGQLError from '../../../Utilities/Urql/Errors/throwGQLError'
import LoadingSpinner from '../../../Components/LoadingSpinner'
import { RadioButton } from '../../../Components/Form/Inputs'
import './EventTypeInput.less'
import useSubmitOnClick from '../../../Hooks/Form/useSubmitOnClick'
import ValidationMessage from '../../../Components/Form/Inputs/ValidationMessage/ValidationMessage'
import getValidationMessage from '../../../Components/Form/Inputs/Generic/Multi/Validation/getValidationMessage'
import getForcedLayout from './Logic/getForcedLayout'

export const eventTypeLabels: Record<string, string> = {
  'Meeting/Workshop': 'Meeting/workshop💡',
  Party: 'Party 🎉',
  'Private Dining': 'Private Dining 🍽',
  'Team Activity': 'Team Activity 🏅',
  Presentation: 'Presentation 📽',
  Wedding: 'Wedding 💖',
  Networking: 'Networking 🤝',
  Studio: 'Studio 📷',
  Conference: 'Conference 🎤',
  'Something Else': 'Something else 🤷‍♀️',
}

const getInputID = (eventTypeID: string) => `event-type-${eventTypeID}`

const triggerConfetti = async (eventTypeID: string) => {
  const input = document.getElementById(getInputID(eventTypeID))
  const { top, left } = input?.getBoundingClientRect() ?? {}
  const origin =
    top !== undefined && left !== undefined
      ? {
          x: (left + 30) / window.innerWidth,
          y: (top + 20) / window.innerHeight,
        }
      : { x: 0.5, y: 0.5 }

  await confetti({
    ticks: 110,
    origin,
    disableForReducedMotion: true,
  })
}

const filterTypesByBrief = (eventTypes: Collection[], brief: PartialBrief): Collection[] =>
  isBizBrief(brief) ? eventTypes.filter(({ name }) => name !== 'Wedding') : eventTypes

interface EventTypeFieldsetProps {
  eventTypes: Collection[]
  fetching: boolean
  eventTypeID?: string | null
  onTypeSelect: ChangeEventHandler<HTMLInputElement>
  onTypeClick: MouseEventHandler<HTMLInputElement>
}

const EventTypeFieldset = ({
  fetching,
  eventTypeID,
  eventTypes,
  onTypeSelect,
  onTypeClick,
}: EventTypeFieldsetProps) => {
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)
  const onInvalid: FormEventHandler<HTMLInputElement> = (event) => {
    setInvalidMessage(getValidationMessage(event.currentTarget, 'event type'))
    event.preventDefault()
  }
  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onTypeSelect(e)
    setInvalidMessage(null)
  }
  return (
    <ValidationMessage invalidMessage={invalidMessage}>
      <fieldset aria-busy={fetching} aria-describedby={'fieldset-loading'}>
        {fetching ? (
          <LoadingSpinner progressId='fieldset-loading' />
        ) : (
          <>
            <legend className='visually-hidden'>Select an event type:</legend>
            <ul role='list' className='event-types'>
              {eventTypes.map(({ id, name }) => (
                <li key={id}>
                  <RadioButton
                    id={getInputID(id)}
                    name='event-type'
                    value={id}
                    required
                    checked={id === eventTypeID}
                    onChange={onChange}
                    onClick={onTypeClick}
                    onInvalid={onInvalid}
                    label={eventTypeLabels[name]}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </fieldset>
    </ValidationMessage>
  )
}

const EventTypeInput: InputView = ({ path }): JSX.Element => {
  const { brief } = useBriefContext()
  const [{ fetching, error, data }] = useEventTypesQuery({ requestPolicy: 'cache-first' })
  const [eventTypeID, setEventTypeID] = useState(brief.collection)
  const [eventTypeName, setEventTypeName] = useState(brief.collectionName)
  const { submitOnClick, formRef } = useSubmitOnClick()

  if (error) throwGQLError(error)

  const eventTypeAttrs = {
    collection: eventTypeID,
    collectionName: eventTypeName,
  }
  const { collections } = data ?? { collections: [] }
  const eventTypes = filterTypesByBrief(collections, brief)

  const forcedLayout = getForcedLayout({ ...brief, ...eventTypeAttrs })
  const attrsToUpdate = {
    ...eventTypeAttrs,
    ...(forcedLayout ? { layouts: forcedLayout } : {}),
  }

  useEffect(() => {
    if (eventTypes.length === 0) return

    const eventCollection = eventTypes.find(({ id }) => id === eventTypeID)

    if (eventCollection?.name === eventTypeName) return

    // for event types set via collection-id query param
    setEventTypeName(eventCollection?.name)
  }, [eventTypeID, eventTypes])

  const onTypeSelect: ChangeEventHandler<HTMLInputElement> = async ({ target }) => {
    setEventTypeID(target.value)
    const eventLabel = eventTypes.find(({ id }) => id === target.value)?.name
    setEventTypeName(eventLabel)
    if (eventLabel === 'Party') await triggerConfetti(target.value)
  }

  return (
    <FormPage
      metaTitle='Event type - HeadBox'
      heading='What are you planning?'
      path={path}
      formRef={formRef}
      inputAttributes={attrsToUpdate}
      className='event-type-page'>
      <EventTypeFieldset
        eventTypes={eventTypes}
        eventTypeID={eventTypeID}
        fetching={fetching}
        onTypeSelect={onTypeSelect}
        onTypeClick={submitOnClick}
      />
    </FormPage>
  )
}

export default EventTypeInput
